/**
 * {order}の紙の画面 /orders/:id/paper（#233、docs/93 §4.5、Figma ④）。発行したら作成画面からここへ移る（送付）。
 *   左＝紙（下書きは透かし入り。出した紙は保存した PDF。電子承諾でない紙は後ろに署名欄の{orderAck}＝?with=ack）
 *   右＝送信方法［印刷・FAX｜メール｜リンク］と、その方法の枠（送付状・メール・承諾ページのリンク）、電子承諾のスイッチ、承諾の記録
 *   見出し＝題・状態チップ・PDF を保存・主ボタン（印刷／送信／リンクをコピー）・⋯（取消・紙で回収・リンクを出し直す・{orderAck}）
 *   送付の記録は今のまま（document_deliveries。リンクは method=link）。スマホ（767px 以下）は紙を見るだけ
 *   約款（#233 2c、docs/93 §14.2）：印刷・FAX は約款を本体と分けた別紙（送付状の枠のスイッチ「約款」＝既定は会社の「印刷で約款」・この1回だけ）。
 *     基本契約書の記録のある{vendor}にはスイッチを出さない。オフ（と約款の無い建設の{order}）は薄墨の1行。メール・リンクは保存した紙のまま（約款は後ろ）
 *   訂正版（#233 2b、docs/93 §14.5）：承諾済の{order}は「⋯」に「訂正版作成」（作成画面へ）。訂正版の下書きは下書きの紙（透かし・送付なし）。
 *     2回目からはスイッチ「変更点」（最後に前回からの変更点のページ。既定オン・{vendor}ごとに前回の選択を覚える）＝印刷・保存・メールの同封書類も同じ紙
 */
import { CONFLICT_MSG, MAIL_MSG, MSG, ORDER_MSG, ORDER_REVISE_MSG, PAPER_MSG, PAYMENT_NOTICE_MSG, PURCHASE_MSG, coverItemKey, orderStampNeeded, type DeliveryItemRef, type Order, type PaperPresetList, type PaymentNotice, type Purchase, type UnissueResult } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { ApiError, api, apiUpload, fetchFile, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { CoverBundlePicker, useItemPages } from "../../components/CoverBundle.jsx";
import { CoverLetterPanel, contentOf, useCoverLetter } from "../../components/CoverLetterPanel.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { MailPanel, MailPreview, MailSendDialog, useMailDelivery } from "../../components/MailPanel.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { UnissueDialog } from "../../components/UnissueDialog.jsx";
import { ErrorText, Help, Switch, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { mdSlash, todayJst } from "../../lib/num.js";
import { usePdfTab } from "../../lib/pdfOpen.js";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../../lib/pdfPages.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { OrderStateChip, jstDay } from "./parts.jsx";

type Via = "print" | "mail" | "link";

/** 選んでいる書式の由来（{vendor}の指定・会社の既定）。#233 2d */
function orderSourceTag(paper: { source: string; ref: string }, ref: string): string {
  if (ref !== paper.ref) return "";
  return paper.source === "partner" ? t("（この{vendor}の指定）") : paper.source === "tenant" ? "（会社の既定）" : "";
}

export function OrderPaperPage() {
  const { id = "" } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const pdfTab = usePdfTab();
  const q = useQuery({ queryKey: ["order", id], queryFn: () => api<Order>("GET", `/orders/${id}`), retry: false });
  const o = q.data;
  const label = o?.names.order ?? t(ORDER_MSG.list);
  const short = o ? docShort(label, o.displayNo ?? o.documentNo) : null;
  usePageName(label, short);
  const back = useBackNav({ to: `/orders/${id}`, label: short ?? label });
  const narrow = useNarrow();
  const pc = !narrow && currentView() !== "phone";
  // 訂正版の下書き（#233 2b）は下書きの紙（透かし・送付なし）
  const draft = o?.status === "draft" || !!o?.revising;
  const issued = o?.status === "issued" && !o?.revising;
  // 紙で受け取る{vendor}（電子承諾でない・まだ承諾の記録が無い）には署名欄の{orderAck}を付けた紙。承諾なしで発行した回は付けない（#253）
  const withAck = !!o && issued && !o.acceptance && !o.link?.active && o.ack.issued !== "none";
  // 初めの送信方法：移ってきたときに決めた方法 → {vendor}の送信設定の「送信方法」（#303 B2。送付状の初めの値と一緒に届く＝下の useEffect）
  const [viaSel, setVia] = useState<Via | null>(() => {
    const v = (loc.state as { via?: string } | null)?.via;
    return v === "mail" || v === "link" ? v : null;
  });
  // リンクは承諾の前だけ（承諾・紙で回収のあとはもう渡さない＝送信方法から外して印刷に戻す）。
  // 電子で発行した回か、紙から電子に切り替えて鍵が生きている回だけ（なし・紙の回には出さない＝#253）
  const linkOk = !!o && !o.acceptance && (o.ack.issued === "electronic" || !!o.link?.active);
  const via: Via = viaSel === "link" && !linkOk ? "print" : (viaSel ?? "print");
  // 印刷・FAX の約款の別紙（2c）：この1回だけ（初めは会社の「印刷で約款」）
  const [termsSel, setTermsSel] = useState<boolean | null>(null);
  const termsSwitch = !!o && o.termsPrint.mode === "switch";
  const termsOn = termsSel ?? o?.termsPrint.default ?? false;
  const printTerms = via === "print" && termsSwitch;
  // 訂正版の「変更点」のページ（#233 2b）：2回目から。初めは{vendor}ごとの前回の選択（既定オン）。変えると覚える
  const [changesSel, setChangesSel] = useState<boolean | null>(null);
  const changesSwitch = !!o && issued && o.revision.no >= 2;
  const changesOn = changesSwitch && (changesSel ?? o?.changeSheetDefault ?? true);
  const setChanges = (v: boolean) => {
    setChangesSel(v);
    void api("POST", `/orders/${id}/change-sheet`, { on: v }).catch((e) => toast(messageOf(e), undefined, "error"));
  };
  // 書式（#233 2d）：下書きは選んだ書式で描き直す（URL の paper は描き直しの印＝API は下書きの選びで描く）
  const paperRef = draft ? o?.paper?.ref : undefined;
  const pdfQuery = new URLSearchParams({ ...(withAck ? { with: "ack" } : {}), ...(printTerms ? { terms: termsOn ? "1" : "0" } : {}), ...(changesOn ? { changes: "1" } : {}), ...(paperRef ? { paper: paperRef } : {}) }).toString();
  const pdfUrl = `/api/v1/orders/${id}/pdf${pdfQuery ? `?${pdfQuery}` : ""}`;
  // 書式の一覧（{order}のプリセット＋自分の書式）と、下書きで選ぶ（#233 2d。版つき＝ぶつかったら最新に重ねて送り直す）
  const presets = useQuery({ queryKey: ["paper-presets"], queryFn: () => api<PaperPresetList>("GET", "/paper/presets"), enabled: !!o });
  const pickPaper = useMutation({
    mutationFn: async (ref: string) => {
      const send = (version: string | undefined) => api<Order>("PUT", `/orders/${id}/paper`, { ref, ...(version ? { version } : {}) });
      try {
        return { d: await send(o?.updatedAt), merged: false };
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 409 && e.code?.endsWith("_STALE"))) throw e;
        const latest = await api<Order>("GET", `/orders/${id}`);
        return { d: await send(latest.updatedAt), merged: true };
      }
    },
    onSuccess: ({ d, merged }) => {
      qc.setQueryData(["order", id], d);
      if (merged) toast(CONFLICT_MSG.autoMerged);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const target = o && issued && pc ? { items: [{ kind: "order" as const, id, ...(termsSwitch ? { terms: termsOn } : {}), ...(changesOn ? { changes: true } : {}) }] as DeliveryItemRef[] } : null;
  const cover = useCoverLetter(via === "print" ? target : null);
  // {vendor}の送信方法の既定（#303 B2）：初めの値が届いたら1回だけ。届くまで送信方法は選べない形で待つ（押す所をずらさない＝#179）
  const viaKnown = viaSel !== null || !target || cover.draft.isError;
  useEffect(() => {
    if (viaSel === null && cover.draft.data) setVia(cover.draft.data.sendMethod);
  }, [viaSel, cover.draft.data]);
  const coverOn = !!target && via === "print" && cover.on;
  // メール：同じ送付先の書類はこの画面の中で足す（見積書の紙の画面と同じ）
  const [mailExtra, setMailExtra] = useState<DeliveryItemRef[]>([]);
  const [mailPicking, setMailPicking] = useState(false);
  const mailTarget = via === "mail" && target ? { items: [...target.items, ...mailExtra] } : null;
  const extraPages = useItemPages(via === "mail" ? mailExtra : []);
  const moveExtra = (i: number, dir: -1 | 1) =>
    setMailExtra((xs) => {
      const j = i + dir;
      if (j < 0 || j >= xs.length) return xs;
      const next = xs.slice();
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const mail = useMailDelivery(mailTarget);
  const mailCover = useCoverLetter(mailTarget, { mail: true });
  const mailCoverOn = via === "mail" && mailCover.on;

  const [pages, setPages] = useState<PageImg[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  // PDF を取って描く（出した紙は保存したもの＝作り直さない）
  useEffect(() => {
    if (!o) return;
    let alive = true;
    let made: string | null = null;
    setPages([]);
    setError(undefined);
    setLoading(true);
    setBlobUrl(null);
    (async () => {
      try {
        const blob = await fetchFile(pdfUrl);
        if (!alive) return;
        made = URL.createObjectURL(blob);
        setBlobUrl(made);
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          if (!alive) return;
          setPages((ps) => {
            const next = ps.slice();
            next[i - 1] = p;
            return next;
          });
        });
      } catch (e) {
        if (alive) setError(messageOf(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [o?.revision.id, o?.status, pdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  useEffect(() => {
    if (!printPages) return;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(r);
  }, [printPages]);
  const [busy, setBusy] = useState(false);
  const refresh = (next: Order) => {
    qc.setQueryData(["order", id], next);
    void qc.invalidateQueries({ queryKey: ["orders"] });
    void qc.invalidateQueries({ queryKey: ["deliveries"] });
  };
  const deliverCover = async (method: "print" | "download") => {
    setBusy(true);
    try {
      const { result, blob } = await cover.deliver(method);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      if (method === "download") downloadBlob(blob, result.fileName);
      else {
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        setPrintPages(await portraitForPrint(got));
      }
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const doPrint = () => {
    if (!pages.length) return;
    if (coverOn) return void deliverCover("print");
    setPrintPages(null);
    window.print();
  };
  /* ---- メール ---- */
  const [mailConfirm, setMailConfirm] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailError, setMailError] = useState<string | undefined>();
  const sendMail = async () => {
    setMailBusy(true);
    setMailError(undefined);
    try {
      await mail.send(mailCoverOn && mailCover.form ? contentOf(mailCover.form) : null);
      setMailConfirm(false);
      toast(MAIL_MSG.sent);
    } catch (e) {
      setMailConfirm(false);
      setMailError(messageOf(e));
    } finally {
      setMailBusy(false);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      void qc.invalidateQueries({ queryKey: ["mail-draft"] });
      // 送ったら状態が「未送付」→「承諾待ち 0日」に変わる（チップを読み直す）
      void qc.invalidateQueries({ queryKey: ["order", id] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    }
  };
  /* ---- リンク（コピー・LINE・SMS） ---- */
  const linkUrl = o?.link?.active ? o.link.url : null;
  const sent = async (v: "copy" | "line" | "sms") => {
    try {
      refresh(await api<Order>("POST", `/orders/${id}/ack/sent`, { via: v }));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** クリップボードに書く。断られたら（ブラウザの許可・窓のフォーカス）リンクの欄を選んで execCommand で写す。それも駄目なら欄を選んだまま知らせる */
  const copyLink = async () => {
    if (!linkUrl) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(linkUrl);
      ok = true;
    } catch {
      const el = document.querySelector<HTMLInputElement>("[data-testid=order-link-url]");
      if (el) {
        el.focus();
        el.select();
        try {
          ok = document.execCommand("copy");
        } catch {
          ok = false;
        }
      }
    }
    if (!ok) {
      toast(ORDER_MSG.linkCopyFailed, undefined, "error", "link-copy-failed");
      return;
    }
    toast(ORDER_MSG.linkCopied, undefined, "success", "link-copied");
    await sent("copy");
  };
  const linkText = o && linkUrl ? `${o.names.order} ${o.displayNo ?? o.documentNo ?? ""}\n${linkUrl}` : "";
  const setEAck = async (v: boolean) => {
    try {
      refresh(await api<Order>("POST", `/orders/${id}/ack/mode`, { eAck: v }));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const reissue = async () => {
    try {
      refresh(await api<Order>("POST", `/orders/${id}/ack/reissue-link`, {}));
      toast(ORDER_MSG.linkReissued);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 取消し（取消しの窓＝見積書・請求書と同じ UnissueDialog。#233 §13.7 ハ） */
  const [unissueDlg, setUnissueDlg] = useState<{ busy: boolean; error?: string | undefined } | null>(null);
  const unissue = async () => {
    setUnissueDlg({ busy: true });
    try {
      const r = await api<UnissueResult>("POST", `/orders/${id}/unissue`, {});
      void qc.invalidateQueries({ queryKey: ["orders"] });
      await qc.invalidateQueries({ queryKey: ["order", id] });
      setUnissueDlg(null);
      nav(`/orders/${id}`, { state: { notice: t(ORDER_MSG.unissued).replace("{no}", r.documentNo), noticeTestId: "unissued-notice" } });
    } catch (e) {
      setUnissueDlg({ busy: false, error: messageOf(e) });
    }
  };
  /** 訂正版を作成（#233 2b）：枝番の下書きを作って作成画面へ */
  const revise = async () => {
    try {
      const next = await api<Order>("POST", `/orders/${id}/revise`, {});
      qc.setQueryData(["order", id], next);
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["order-rounds", id] });
      nav(`/orders/${id}`, { state: { notice: ORDER_REVISE_MSG.reviseDone.replace("{no}", next.displayNo ?? ""), noticeTestId: "revise-created" } });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 仕入を登録（#233 3a、docs/93 §15.10 ①-a）：承諾済の{order}から明細を写して仕入の下書き → 作成画面へ */
  const createPurchase = async () => {
    try {
      const pu = await api<Purchase>("POST", "/purchases", { fromOrderId: id });
      void qc.invalidateQueries({ queryKey: ["purchases"] });
      nav(`/purchases/${pu.id}`);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** {paymentNotice}を作成（#233 3b、docs/93 §15.6）：承諾済の{order}から行と件名を写して下書き → 作成画面へ */
  const createNotice = async () => {
    try {
      const pn = await api<PaymentNotice>("POST", "/payment-notices", { fromOrderId: id });
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
      nav(`/payment-notices/${pn.id}`);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /* ---- 紙で回収 ---- */
  const [paperDlg, setPaperDlg] = useState<{ on: string; file: File | null; busy: boolean; error?: string | undefined } | null>(null);
  const savePaper = async () => {
    if (!paperDlg) return;
    setPaperDlg({ ...paperDlg, busy: true, error: undefined });
    try {
      const copy = paperDlg.file ? await apiUpload<{ assetId: string }>(`/orders/${id}/ack/copy`, paperDlg.file, "application/pdf") : null;
      refresh(await api<Order>("POST", `/orders/${id}/ack/paper`, { receivedOn: paperDlg.on, copyAssetId: copy?.assetId ?? null }));
      setPaperDlg(null);
      toast(t(ORDER_MSG.paperAckDone).replace("{orderAck}", o?.names.orderAck ?? ""), undefined, "success", "paper-ack-done");
    } catch (e) {
      setPaperDlg((d) => (d ? { ...d, busy: false, error: messageOf(e) } : d));
    }
  };

  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/orders">{t(ORDER_MSG.list)}</Link>
        </p>
      </div>
    );
  }
  const ackName = o?.names.orderAck ?? "";
  const subject = o?.revision.subject || label;
  const docNo = o ? (o.displayNo ?? o.documentNo) : null;
  const fileName = o ? `${label}_${docNo && !draft ? docNo : "下書き"}_${subject}.pdf` : `${label}.pdf`;
  const actions: HeadAction[] = !o
    ? []
    : [
        // 仕入を登録（#233 3a）：承諾済か承諾なしで発行した{order}だけ（#253＝ackDone）・PC だけ（スマホは閲覧のみ）。「⋯」のいちばん上
        ...(issued && o.ackDone && pc ? [{ label: PURCHASE_MSG.orderMenuCreate, onSelect: () => void createPurchase(), testId: "order-purchase-create", group: "state" as const }] : []),
        // {paymentNotice}を作成（#233 3b）：承諾済の{order}だけ・PC だけ。請求書を出さない{vendor}に、元請が作って渡す仕入明細書
        ...(issued && o.ackDone && pc ? [{ label: t(PAYMENT_NOTICE_MSG.orderMenuCreate), onSelect: () => void createNotice(), testId: "order-notice-create", group: "state" as const }] : []),
        ...(issued && !o.acceptance && o.canUnissue ? [{ label: ORDER_MSG.unissue, onSelect: () => setUnissueDlg({ busy: false }), testId: "order-unissue", group: "state" as const }] : []),
        ...(issued && !o.acceptance && o.ack.issued !== "none" ? [{ label: ORDER_MSG.paperAck, onSelect: () => setPaperDlg({ on: todayJst(), file: null, busy: false }), testId: "order-paper-ack", group: "state" as const }] : []),
        ...(issued && !o.acceptance && o.link ? [{ label: ORDER_MSG.linkReissue, onSelect: () => void reissue(), testId: "order-link-reissue", group: "state" as const }] : []),
        ...(issued && (o.acceptance || o.ack.issued !== "none") ? [{ label: t(ORDER_MSG.ackPdf).replace("{orderAck}", ackName), onSelect: () => void pdfTab.open(`/api/v1/orders/${id}/ack.pdf`), testId: "order-ack-pdf", group: "doc" as const }] : []),
        // 訂正版（#233 2b）：承諾済（画面・紙で回収）だけ。作成すると作成画面で枝番の下書きを直す
        ...(issued && o.canRevise && pc ? [{ label: ORDER_REVISE_MSG.revise, onSelect: () => void revise(), testId: "order-revise", group: "state" as const }] : []),
        ...(o.revision.no >= 2 ? [{ label: ORDER_REVISE_MSG.compare, to: `/orders/${id}/compare?from=${o.revision.no - 1}&to=${o.revision.no}`, testId: "order-compare", group: "doc" as const }] : []),
      ];
  return (
    <div className="paperview">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{label}</h1>
        {o ? <OrderStateChip o={o} /> : null}
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {o ? `${docNo && !draft ? docNo + "　" : ""}${subject}` : "読み込んでいます…"}
        </span>
        <span className="sp" />
        {o?.paper ? (
          <select
            className="pdfform papersel"
            aria-label="書式"
            data-testid="paper-select"
            value={o.paper.ref}
            disabled={o.paper.frozen || pickPaper.isPending}
            title={o.paper.frozen ? PAPER_MSG.issued : t("書式（会社の既定と{vendor}ごとの指定から決まります。ここで選ぶとこの下書きに残ります）")}
            onChange={(e) => pickPaper.mutate(e.target.value)}
          >
            {(presets.data?.presets.filter((p) => p.kinds.includes("order")) ?? [{ key: o.paper.preset, name: o.paper.name }]).map((p) => (
              <option key={p.key} value={`preset:${p.key}`}>
                {t(p.name)}
                {orderSourceTag(o.paper!, `preset:${p.key}`)}
              </option>
            ))}
            {(() => {
              const own = presets.data?.own.order ?? [];
              const list = o.paper.own && !own.some((x) => `template:${x.id}` === o.paper!.ref) ? [...own, { id: o.paper.ref.slice(9), name: o.paper.name, orient: o.paper.orient }] : own;
              return list.length ? (
                <optgroup label="カスタム書式">
                  {list.map((x) => (
                    <option key={x.id} value={`template:${x.id}`}>
                      {x.name}（{x.orient === "landscape" ? "横" : "縦"}）{orderSourceTag(o.paper!, `template:${x.id}`)}
                    </option>
                  ))}
                </optgroup>
              ) : null;
            })()}
          </select>
        ) : null}
        {coverOn ? (
          <button type="button" className="btn" disabled={!blobUrl || busy || !cover.ready} onClick={() => void deliverCover("download")} data-testid="paper-save">PDF 保存</button>
        ) : (
          <a className={"btn" + (blobUrl ? "" : " disabled")} href={blobUrl ?? undefined} download={fileName} aria-disabled={!blobUrl} data-testid="paper-save">PDF 保存</a>
        )}
        {target && via === "mail" ? (
          <button type="button" className="btn primary" onClick={() => setMailConfirm(true)} disabled={!mail.ready || mail.problems.length > 0 || mailBusy || mail.uploading || (mailCoverOn && !mailCover.ready)} data-testid="mail-send">
            {mailBusy ? MAIL_MSG.sending : MAIL_MSG.send}
          </button>
        ) : target && via === "link" ? (
          <button type="button" className="btn primary" onClick={() => void copyLink()} disabled={!linkUrl} data-testid="order-link-copy">{ORDER_MSG.linkCopy}</button>
        ) : (
          <button type="button" className="btn primary" onClick={doPrint} disabled={loading || !pages.length || !!error || busy || (coverOn && !cover.ready)} data-testid="paper-print">
            {busy ? "送付状作成中…" : "印刷"}
          </button>
        )}
        {/* ⋯ は .hd-acts の中（幅 40px と点の大きさ。前は外にあって点の無い細い枠になっていた） */}
        {actions.length ? <span className="hd-acts" data-testid="head-actions"><HeadActions actions={actions} label={t(ORDER_MSG.stateMenu)} /></span> : null}
      </div>
      <ErrorText>{error}</ErrorText>
      {loading && !pages.length && !error ? <p className="hint" role="status" data-testid="paper-making">{MSG.pdfMaking}</p> : null}
      <div className={target ? "paperwrap" : undefined}>
        <div className="pages" aria-label={`${label}の紙`}>
          {target && via === "mail" ? <MailPreview m={mail} companyName={mail.draft.data?.values.company ?? ""} /> : null}
          {coverOn || mailCoverOn
            ? (via === "mail" ? mailCover : cover).pages.map((p, i) => (
                <div className="clsheet" key={`c${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {pages.map((p, i) =>
            p ? (
              <div className="paperpage" key={i}>
                <img src={p.url} alt={`${subject} ${i + 1}ページ目`} width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
          {via === "mail"
            ? extraPages.flatMap((q2, k) =>
                (q2.data ?? []).map((p, i) => (
                  <div className="paperpage" key={`x${k}-${i}`} data-testid="mail-extra-sheet">
                    <img src={p.url} alt={`追加した書類 ${k + 1} の ${i + 1}ページ目`} width={p.w} height={p.h} />
                  </div>
                )),
              )
            : null}
        </div>
        {target && o ? (
          <div className="sendcol" data-testid="send-column">
            <div className={"clpanel sendvia" + (viaKnown ? "" : " is-wait")} aria-busy={viaKnown ? undefined : true}>
              <span className="cllbl" id="osendvia-l">{ORDER_MSG.via}</span>
              <Segmented
                options={[
                  { value: "print" as const, label: MAIL_MSG.viaPrint, testId: "send-via-print" },
                  { value: "mail" as const, label: MAIL_MSG.viaMail, testId: "send-via-mail" },
                  ...(linkOk ? [{ value: "link" as const, label: ORDER_MSG.viaLink, testId: "send-via-link" }] : []),
                ]}
                value={via}
                onChange={setVia}
                labelledBy="osendvia-l"
                testId="send-via"
              />
            </div>
            <AckPanel o={o} onEAck={(v) => void setEAck(v)} openPdf={(u) => void pdfTab.open(u, { stored: true })} busy={!!pdfTab.busy} />
            {changesSwitch ? (
              <div className="clpanel" data-testid="order-changes-panel">
                <span className="row" style={{ alignItems: "center", gap: 6 }}>
                  <Switch checked={changesOn} onChange={setChanges} label={ORDER_REVISE_MSG.changeSheet} testId="order-changes-switch" />
                  <Help label={`${ORDER_REVISE_MSG.changeSheet}の説明`} text={t(ORDER_REVISE_MSG.changeSheetHelp)} testId="help-order-changes" />
                </span>
              </div>
            ) : null}
            {via === "mail" ? (
              <>
                <MailPanel m={mail} bundle={{ extra: mailExtra, onMove: moveExtra, onRemove: (i) => setMailExtra((xs) => xs.filter((_, j) => j !== i)), onAddDocs: () => setMailPicking(true) }} error={mailError} />
                <CoverLetterPanel c={mailCover} showWindow={false} testId="mail-cover-panel" />
              </>
            ) : via === "link" ? (
              <div className="clpanel olink" data-testid="order-link-panel">
                <div className="cllbl">{ORDER_MSG.viaLink}</div>
                {linkUrl ? (
                  <>
                    <input className="inp" readOnly value={linkUrl} aria-label={ORDER_MSG.viaLink} onFocus={(e) => e.target.select()} data-testid="order-link-url" />
                    <p className="hint" style={{ margin: "6px 0" }}>{ORDER_MSG.linkExpires.replace("{date}", mdSlash(jstDay(o.link!.expiresAt)))}</p>
                    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                      <a className="btn sm" href={`https://line.me/R/msg/text/?${encodeURIComponent(linkText)}`} target="_blank" rel="noreferrer" onClick={() => void sent("line")} data-testid="order-link-line">{ORDER_MSG.linkLine}</a>
                      <a className="btn sm" href={`sms:?&body=${encodeURIComponent(linkText)}`} onClick={() => void sent("sms")} data-testid="order-link-sms">{ORDER_MSG.linkSms}</a>
                    </div>
                  </>
                ) : (
                  <p className="hint" style={{ margin: 0 }}>{t(ORDER_MSG.linkNone)}</p>
                )}
              </div>
            ) : (
              <CoverLetterPanel
                c={cover}
                terms={termsSwitch ? { rows: [{ id, label: ORDER_MSG.termsPrint, on: termsOn }], onChange: (_id, v) => setTermsSel(v) } : undefined}
              />
            )}
          </div>
        ) : null}
      </div>
      {mailTarget && mailPicking ? (
        <CoverBundlePicker
          target={mailTarget}
          onClose={() => setMailPicking(false)}
          onPick={(refs, labels) => {
            setMailPicking(false);
            setMailExtra((xs) => [...xs, ...refs.filter((r) => !xs.some((x) => coverItemKey(x) === coverItemKey(r))).map((r) => ((r.kind === "received" || r.kind === "file") && labels[coverItemKey(r)] ? { ...r, label: labels[coverItemKey(r)] } : r))]);
          }}
        />
      ) : null}
      {mailConfirm && mail.form ? <MailSendDialog m={mail} cover={mailCoverOn} busy={mailBusy} onClose={() => setMailConfirm(false)} onSend={() => void sendMail()} /> : null}
      {unissueDlg && o ? <UnissueDialog doc="order" no={o.displayNo ?? o.documentNo ?? ""} busy={unissueDlg.busy} error={unissueDlg.error} onClose={() => setUnissueDlg(null)} onConfirm={() => void unissue()} /> : null}
      {paperDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !paperDlg.busy && setPaperDlg(null)}>
          <div className="dlg" role="dialog" aria-label={ORDER_MSG.paperAckTitle} onKeyDown={(e) => e.key === "Escape" && setPaperDlg(null)} data-testid="order-paper-ack-dialog">
            <div className="dh">{ORDER_MSG.paperAckTitle}</div>
            <div className="db">
              <label className="olbl" htmlFor="opa_on">{ORDER_MSG.paperAckOn}</label>
              <DateInput id="opa_on" label={ORDER_MSG.paperAckOn} value={paperDlg.on} clearable={false} onChange={(v) => v && setPaperDlg({ ...paperDlg, on: v })} testId="order-paper-ack-on" />
              <label className="olbl" htmlFor="opa_copy" style={{ marginTop: 12 }}>{ORDER_MSG.paperAckCopy}</label>
              <input id="opa_copy" type="file" accept="application/pdf,.pdf" onChange={(e) => setPaperDlg({ ...paperDlg, file: e.target.files?.[0] ?? null })} data-testid="order-paper-ack-file" />
              <ErrorText>{paperDlg.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={paperDlg.busy} onClick={() => setPaperDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={paperDlg.busy || !paperDlg.on} onClick={() => void savePaper()} data-testid="order-paper-ack-save">{ORDER_MSG.paperAckSave}</button>
            </div>
          </div>
        </div>
      ) : null}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(coverOn && printPages ? printPages : pages).map((p, i) =>
            p ? (
              <div className="printedPage" key={i}>
                <img src={p.url} alt="" width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** 右の枠の「承諾」：電子承諾のスイッチ（承諾の前だけ）と、承諾の記録（承諾のあと） */
function AckPanel({ o, onEAck, openPdf, busy }: { o: Order; onEAck: (v: boolean) => void; openPdf: (url: string) => void; busy: boolean }) {
  const a = o.acceptance;
  // 訂正版（#233 2b）：前の回の承諾の記録番号（前の回の紙と承諾の記録は作成画面の回の帯から開ける）
  const prev = o.prevRound?.recordNo ? (
    <p className="hint" style={{ margin: "4px 0 0" }} data-testid="order-prev-ack">
      {o.prevRound.displayNo}　{ORDER_REVISE_MSG.prevAck.replace("{rec}", "")}
      <span className="num" style={{ whiteSpace: "nowrap" }}>{o.prevRound.recordNo}</span>
    </p>
  ) : null;
  if (a) {
    return (
      <div className="clpanel oackrec" data-testid="order-ack-record">
        <div className="cllbl">{ORDER_MSG.ackRecord}</div>
        <dl className="odl">
          <dt>{ORDER_MSG.ackMethod}</dt>
          <dd>{a.method === "paper" ? ORDER_MSG.ackPaper : ORDER_MSG.ackScreen}</dd>
          <dt>{ORDER_MSG.ackAt}</dt>
          <dd data-testid="order-ack-at">{a.method === "paper" && a.receivedOn ? a.receivedOn.replace(/-/g, "/") : new Date(a.at).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })}</dd>
          {a.email ? (
            <>
              <dt>{ORDER_MSG.ackBy}</dt>
              <dd>{a.email}</dd>
            </>
          ) : null}
          <dt>{ORDER_MSG.ackRecordNo}</dt>
          <dd className="num" data-testid="order-ack-no">{a.recordNo}</dd>
        </dl>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn sm" disabled={busy} onClick={() => openPdf(`/api/v1/orders/${o.id}/ack.pdf`)} data-testid="order-ack-pdf-open">{busy ? MSG.pdfMaking : t(ORDER_MSG.ackPdf).replace("{orderAck}", o.names.orderAck)}</button>
          {a.hasCopy ? <button type="button" className="btn sm text" disabled={busy} onClick={() => openPdf(`/api/v1/orders/${o.id}/ack/copy`)}>{ORDER_MSG.paperAckCopy.replace("（PDF・任意）", "")}</button> : null}
        </div>
        {prev}
      </div>
    );
  }
  // 承諾なしで発行した回（#253）は承諾の枠を出さない
  if (o.ack.issued === "none" || o.status !== "issued") return prev ? <div className="clpanel">{prev}</div> : null;
  // 紙で受け取る回の収入印紙（#252）：請負・税抜 1万円以上だけ
  const stamp = orderStampNeeded({ ack: "paper", construction: o.construction, workKind: o.workKind, subtotal: o.revision.subtotal });
  return (
    <div className="clpanel" data-testid="order-ack-panel">
      <Switch checked={!!o.link?.active} onChange={onEAck} label={ORDER_MSG.eAck} testId="order-eack-switch" />
      {o.link?.active ? <p className="hint" style={{ margin: "4px 0 0" }}>{ORDER_MSG.identity}</p> : stamp ? <p className="hint" style={{ margin: "4px 0 0" }} data-testid="order-paper-stamp">{t(ORDER_MSG.paperStamp)}</p> : null}
      {prev}
    </div>
  );
}
